/**
 * Operator audit-log calls (read-only). Shapes follow the frozen contract of
 * GET /operator/audit/*. Summaries are redacted by the server.
 */
import type { AuditFilter, AuditPeriod } from "../adminRoute";
import { operatorGet, queryString, type OperatorFetch } from "../operatorHttp";

export const AUDIT_PAGE_SIZE = 50;

export interface AuditActor {
  id: string;
  kind: "operator" | "other";
  display_name: string | null;
}

export interface AuditEventItem {
  event_id: string;
  event_type: string;
  occurred_at: string;
  outcome: string;
  safe_reason: string | null;
  actor: AuditActor;
  resource_type: string;
  resource_id: string | null;
  correlation_id: string | null;
}

export interface AuditEventDetailData extends AuditEventItem {
  schema_version: number | string;
  causation_id: string | null;
  idempotency_ref: string | null;
  created_at: string | null;
  summary: Record<string, unknown>;
}

export interface AuditEventsPage {
  items: AuditEventItem[];
  next_cursor: string | null;
}

export interface AuditQuery {
  filter: AuditFilter;
  cursor?: string | null;
}

export interface AuditApi {
  list(query: AuditQuery): Promise<AuditEventsPage>;
  detail(eventId: string): Promise<AuditEventDetailData>;
  eventTypes(): Promise<string[]>;
}

const HOUR_MS = 3_600_000;
const WINDOW_MS: Record<Exclude<AuditPeriod, "all">, number> = {
  "24h": 24 * HOUR_MS,
  "7d": 7 * 24 * HOUR_MS,
  "30d": 30 * 24 * HOUR_MS,
};

/** Start of the period as ISO text; `all` has no lower bound. */
export function periodFrom(period: AuditPeriod, now: Date): string | undefined {
  return period === "all"
    ? undefined
    : new Date(now.getTime() - WINDOW_MS[period]).toISOString();
}

export function createAuditApi(
  fetcher: OperatorFetch = (...args) => fetch(...args),
): AuditApi {
  return {
    list: ({ filter, cursor }) =>
      operatorGet(
        fetcher,
        `/audit/events${queryString({
          operator_id: filter.operator,
          event_type: filter.type,
          user_id: filter.user,
          outcome: filter.outcome,
          from: periodFrom(filter.period ?? "7d", new Date()),
          cursor,
          limit: AUDIT_PAGE_SIZE,
        })}`,
      ),
    detail: (eventId) =>
      operatorGet(fetcher, `/audit/events/${encodeURIComponent(eventId)}`),
    eventTypes: async () =>
      (await operatorGet<{ items: string[] }>(fetcher, "/audit/event-types"))
        .items,
  };
}
